import { useMutation, useQueryClient } from '@tanstack/react-query';

import ApiCart from '@Api/Cart';
import findPositionById from './helpers/findPositionById';
import transformCart from './helpers/transformCart';
import useCartKeys from './useCartKeys';

import type { CartData } from '@Types/Cart';

export interface Variables {
  positionId: string;
  options: { isRelated: boolean };
}

export const useRemovePositionFromCart = () => {
  const queryClient = useQueryClient();
  const keys = useCartKeys();
  const cartData: CartData = queryClient.getQueryData(keys);

  const mutation = useMutation<CartData, Error, Variables>({
    mutationFn: async ({ positionId, options }) => {
      const position = findPositionById(positionId, cartData);
      const res = await ApiCart.remove({ cartPositionId: position.id });

      return transformCart({ cart: res, options });
    },
    mutationKey: ['removeFromCart'],
    onMutate: async ({ positionId }) => {
      const position = findPositionById(positionId, cartData);

      if (!position) return;

      await queryClient.cancelQueries({ queryKey: keys });

      // Снимок предыдущего значения
      const prevCart: CartData = queryClient.getQueryData(keys);

      const newPositionsList = prevCart.positions.filter((pos) => position.id !== pos.id);

      const removedPositionsList = prevCart.removedPositions.filter(
        (pos) => pos.id !== position.id,
      );

      const newCart = {
        ...prevCart,
        positions: newPositionsList,
        removedPositions: removedPositionsList,
      };

      // Оптимистическое обновление
      queryClient.setQueryData(keys, () => {
        return newCart;
      });

      // Возвращаем объект контекста с зафиксированным значением
      return { prevCart };
    },
    onError: (err, data, context: { prevCart: CartData }) => {
      queryClient.setQueryData(keys, context.prevCart);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(keys, (prev: CartData) => ({ ...prev, ...cart }));
      queryClient.invalidateQueries({ queryKey: ['cartShortInfo'], refetchType: 'all' });
    },
  });

  return mutation;
};

export default useRemovePositionFromCart;
